import React from 'react'
import { Box, Link, Paper, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'
import type { StatusTone } from './chipPresets'

const TONE_ACCENT: Record<StatusTone, string> = {
  success: poolwerxColors.success,
  warning: poolwerxColors.warning,
  error: poolwerxColors.error,
  info: poolwerxColors.teal,
  neutral: poolwerxColors.grey[400],
}

export interface AppDrawerItemCardProps {
  tone?: StatusTone
  leadingIcon?: React.ReactNode
  title?: React.ReactNode
  subtitle?: React.ReactNode
  chips?: React.ReactNode
  timestamp?: React.ReactNode
  actions?: React.ReactNode
  children?: React.ReactNode
  onClick?: () => void
  href?: string
  external?: boolean
  sx?: SxProps<Theme>
}

const AppDrawerItemCard: React.FC<AppDrawerItemCardProps> = ({
  tone,
  leadingIcon,
  title,
  subtitle,
  chips,
  timestamp,
  actions,
  children,
  onClick,
  href,
  external,
  sx,
}) => {
  const accent = tone ? TONE_ACCENT[tone] : undefined
  const interactive = onClick != null || href != null

  const baseSx: SxProps<Theme> = {
    p: 1.25,
    pl: accent ? 1.75 : 1.25,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    position: 'relative',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    color: 'text.primary',
    textDecoration: 'none',
    cursor: interactive ? 'pointer' : undefined,
    transition: interactive
      ? 'border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease'
      : undefined,
    ...(interactive && {
      '&:hover': {
        borderColor: poolwerxColors.teal,
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 127, 152, 0.08)',
      },
    }),
  }

  const inner = (
    <>
      {accent && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: accent,
          }}
        />
      )}
      <Stack
        direction="row"
        alignItems={subtitle ? 'center' : 'flex-start'}
        spacing={1.25}
      >
        {leadingIcon != null && (
          <Box
            sx={{
              bgcolor: poolwerxColors.tealLight,
              borderRadius: 1,
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: poolwerxColors.teal,
            }}
          >
            {leadingIcon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {title != null && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                whiteSpace: subtitle ? 'normal' : 'nowrap',
                overflow: subtitle ? undefined : 'hidden',
                textOverflow: subtitle ? undefined : 'ellipsis',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}
            >
              {subtitle}
            </Typography>
          )}
          {(chips || timestamp) && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                mt: title != null || subtitle != null ? 0.5 : 0,
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              {chips}
              {timestamp && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 'auto', fontSize: '0.7rem' }}
                >
                  {timestamp}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
        {actions != null && (
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Stack>
      {children}
    </>
  )

  if (href) {
    return (
      <Paper
        variant="outlined"
        component={Link}
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        underline="none"
        sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      >
        {inner}
      </Paper>
    )
  }

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {inner}
    </Paper>
  )
}

export default AppDrawerItemCard
